import { ConnectLogo } from "./connect-logo"
import { Button } from "@/components/ui/button.tsx"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { getIntegrations, upsertIntegration, IntegrationWithJobInfo } from "@/api/integration"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"

export interface ConnectEmailPageProps {
  logo: string
  logoAlt: string
  integrationName: string
  description: string
  redirectTo: string
}

export function ConnectEmailPage({ logo, logoAlt, integrationName, description, redirectTo }: ConnectEmailPageProps) {
  const [emails, setEmails] = useState<IntegrationWithJobInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [, setLocation] = useLocation()

  const currentPath = window.location.pathname

  async function refreshEmails() {
    const res = await getIntegrations()
    if (!res.ok) {
      console.error(res.error)
      return
    }
    setEmails(res.value.data.filter((i: IntegrationWithJobInfo) => ["gmail", "microsoft"].includes(i.name)))
  }

  useAsyncEffect(refreshEmails, [])

  async function handleContinue() {
    if (emails.length === 0) return
    const email = emails[0].username
    setLoading(true)
    const res = await upsertIntegration(integrationName, email, {})
    setLoading(false)
    if (!res.ok) {
      console.error(res.error)
      return
    }
    setLocation(redirectTo)
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6">
        <ConnectLogo src={logo} alt={logoAlt} />
        <p class="text-gray-500 text-sm">{description}</p>

        <ConnectMailboxButtons
          next={currentPath}
          onChange={refreshEmails}
          microsoftLabel="Connect Microsoft Office 365"
        />
        {emails.length > 0 && (
          <div class="border border-gray-200 rounded-lg p-4">
            <h3 class="font-medium text-gray-900 text-sm mb-3">Connected Accounts</h3>
            <div class="space-y-2">
              {emails.map((e: IntegrationWithJobInfo) => {
                let updated = ""
                if (e.session_updated_at) {
                  const d = DateTime.fromISO(e.session_updated_at)
                  updated = d.toLocaleString(DateTime.DATETIME_MED)
                }
                return (
                  <div key={e.username} class="flex justify-between gap-4 text-sm">
                    <span class="text-gray-700 truncate">{e.username}</span>
                    <span class="text-gray-400 whitespace-nowrap">{updated}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <Button className="w-full" onClick={handleContinue} disabled={emails.length === 0 || loading}>
          {loading ? "Creating..." : "Continue"}
        </Button>

        {emails.length === 0 && (
          <p class="text-sm text-gray-400 text-center">Connect at least one email account to continue</p>
        )}
      </div>
    </CenterColumnContainer>
  )
}
